// src/components/DropdownSearch.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import "../styles/dropdown.css";

/* Find scrollable ancestors so we can re-position on their scroll */
function getScrollParents(node) {
  const parents = [];
  if (!node) return parents;
  let p = node.parentNode;
  while (p && p !== document.body && p !== document.documentElement) {
    try {
      const style = window.getComputedStyle(p);
      const oy = style.overflowY, o = style.overflow;
      if (["auto", "scroll"].includes(oy) || ["auto", "scroll"].includes(o)) {
        parents.push(p);
      }
    } catch {}
    p = p.parentNode;
  }
  parents.push(window);
  return parents;
}

/* Body scroll lock helpers */
function lockBodyScroll() {
  const html = document.documentElement;
  const body = document.body;
  // preserve current scroll position
  const scrollY = window.scrollY || document.documentElement.scrollTop;
  body.dataset.prevScrollY = String(scrollY);
  body.style.top = `-${scrollY}px`;
  body.style.position = "fixed";
  body.style.width = "100%";
  html.style.overflow = "hidden";
}

function unlockBodyScroll() {
  const html = document.documentElement;
  const body = document.body;
  const prev = Number(body.dataset.prevScrollY || 0);
  body.style.position = "";
  body.style.top = "";
  body.style.width = "";
  html.style.overflow = "";
  window.scrollTo(0, prev);
  delete body.dataset.prevScrollY;
}

export default function SearchableDropdown({
  options = [],
  value = "",
  onChange,
  placeholder = "Search",
  width = "100%",
  allowAll = false,   // kept for API compatibility
  maxMenuHeight = 260,
  zIndex = 2147483647,
}) {
  // Normalize options -> {label, value}
  const items = useMemo(
    () => options.map((o) => (typeof o === "string" ? { label: o, value: o } : o)),
    [options]
  );

  const rootRef = useRef(null);
  const inputRef = useRef(null);
  const menuRef = useRef(null);

  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0, width: 0 });
  const [highlight, setHighlight] = useState(0);
  const [dropUp, setDropUp] = useState(false);

  const filtered = useMemo(() => {
    const q = (query || "").toLowerCase();
    if (!q) return items;
    return items.filter((it) => it.label.toLowerCase().includes(q));
  }, [query, items]);

  const pick = (it) => {
    if (!it) return;
    onChange?.(it.value);
    setQuery(it.label);
    setOpen(false);
  };

  const recalcPosition = () => {
    const node = rootRef.current;
    if (!node) return;
    const r = node.getBoundingClientRect();
    const topDoc = r.bottom + window.scrollY;
    const leftDoc = r.left + window.scrollX;
    const width = r.width;

    // space above/below in viewport
    const viewportBelow = window.innerHeight - r.bottom;
    const viewportAbove = r.top;

    // measure menu height if mounted
    let menuH = maxMenuHeight;
    if (menuRef.current) {
      menuH = Math.min(maxMenuHeight, menuRef.current.scrollHeight || maxMenuHeight);
    }

    const shouldDropUp = viewportBelow < Math.min(menuH, maxMenuHeight) && viewportAbove > viewportBelow;

    const finalTop = shouldDropUp
      ? r.top + window.scrollY - Math.min(menuH, maxMenuHeight)
      : topDoc;

    setCoords({ top: finalTop, left: leftDoc, width });
    setDropUp(shouldDropUp);
  };

  // Mirror external value into the input
  useEffect(() => {
    if (value && value !== query) setQuery(value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  // Attach scroll/resize listeners and lock/unlock body scroll
  useEffect(() => {
    if (!open) return;
    recalcPosition();

    // Lock body scroll while menu is open
    lockBodyScroll();

    const parents = getScrollParents(rootRef.current);
    const onScroll = () => recalcPosition();
    const onResize = () => recalcPosition();

    parents.forEach((p) => p.addEventListener("scroll", onScroll, { passive: true }));
    window.addEventListener("resize", onResize, { passive: true });

    return () => {
      parents.forEach((p) => p.removeEventListener("scroll", onScroll));
      window.removeEventListener("resize", onResize);
      unlockBodyScroll();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, maxMenuHeight]);

  // Close on outside click
  useEffect(() => {
    const handleDocClick = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target) && open) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleDocClick);
    return () => document.removeEventListener("mousedown", handleDocClick);
  }, [open]);

  // Keyboard navigation (prevents page scroll while open)
  const onKeyDown = (e) => {
    if (!open && (e.key === "ArrowDown" || e.key === "Enter")) {
      setOpen(true);
      setTimeout(recalcPosition, 0);
      e.preventDefault();
      return;
    }
    if (!open) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, filtered.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      pick(filtered[highlight]);
    } else if (e.key === "Escape") {
      e.preventDefault();
      setOpen(false);
    }
  };

  // Wheel/touch handler to stop background scroll when clicking outside the menu
  const stopScrollOutside = (e) => {
    if (!open) return;
    if (!menuRef.current) return;
    // If the wheel/touch is not on the menu area, fully block it
    if (!menuRef.current.contains(e.target)) {
      e.preventDefault();
    }
  };

  // The actual menu (rendered inside a fixed overlay portal)
  const menu = (
    <div
      style={{
        position: "absolute",
        top: coords.top,
        left: coords.left,
        width: coords.width,
        zIndex,
      }}
    >
      <div
        ref={menuRef}
        className={`searchable-dropdown-menu${dropUp ? " drop-up" : ""}`}
        style={{
          maxHeight: maxMenuHeight,
          overflowY: "auto",
          overscrollBehavior: "contain", // stop scroll chaining to body
        }}
        role="listbox"
      >
        {filtered.length === 0 ? (
          <div className="searchable-dropdown-item disabled">No results</div>
        ) : (
          filtered.map((it, idx) => (
            <div
              key={it.value ?? it.label}
              role="option"
              aria-selected={idx === highlight}
              className={
                "searchable-dropdown-item" + (idx === highlight ? " active" : "")
              }
              onMouseDown={(e) => {
                e.preventDefault(); // avoid closing via outside handler first
                pick(it);
              }}
              onMouseEnter={() => setHighlight(idx)}
            >
              {it.label}
            </div>
          ))
        )}
      </div>
    </div>
  );

  return (
    <div
      ref={rootRef}
      className="searchable-dropdown"
      style={{ width, position: "relative" }}
    >
      <div className="searchable-dropdown-inputwrap">
        <input
          ref={inputRef}
          className="searchable-dropdown-input"
          placeholder={placeholder}
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            if (!open) setOpen(true);
          }}
          onFocus={() => {
            setOpen(true);
            setTimeout(recalcPosition, 0);
          }}
          onKeyDown={onKeyDown}
          autoComplete="off"
        />
        <button
          type="button"
          className="searchable-dropdown-caret"
          onClick={() => {
            if (open) setOpen(false);
            else {
              setOpen(true);
              setTimeout(recalcPosition, 0);
            }
          }}
          aria-label="Toggle menu"
        >
          ▾
        </button>
      </div>

      {/* Fixed, full-screen overlay to host the menu and block background scroll */}
      {open
        ? ReactDOM.createPortal(
            <div
              style={{
                position: "fixed",
                inset: 0,
                zIndex,
                // let clicks pass through EXCEPT where our absolutely-positioned menu renders
                pointerEvents: "none",
              }}
              onWheel={stopScrollOutside}
              onTouchMove={stopScrollOutside}
            >
              <div
                style={{
                  position: "relative",
                  width: 0,
                  height: 0,
                  pointerEvents: "auto", // allow interaction with the menu
                }}
              >
                {menu}
              </div>
            </div>,
            document.body
          )
        : null}
    </div>
  );
}
